import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OrderSummary, Paginated, VehicleAssignment } from '@kontor/shared';
import { api, queryString } from '../../api';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { useSession } from '../../session';
import { LIVE_MS, errorText, today, useEmployees, useVehicles, wallFull } from './shared';
import './fleet.css';

type When = 'now' | 'upcoming' | 'past';
const WHEN_LABEL: Record<When, string> = { now: 'läuft', upcoming: 'geplant', past: 'beendet' };
const whenOf = (a: VehicleAssignment, nowWall: string): When => (a.active ? 'now' : a.endsAt <= nowWall ? 'past' : 'upcoming');
const nowWall = () => `${today()}T${new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' })}`;

/**
 * Einsätze eines Fahrzeugs oder eines Auftrags (KON-167): Das Fahrzeug wird für einen Zeitraum eingeplant, optional für einen Auftrag und mit einem Fahrer. Solange der Zeitraum
 * läuft, steht das Fahrzeug in der Flotte auf „Im Einsatz“, danach wieder auf „Frei“. Überschneidungen meldet der Server mit Fahrzeug, Zeit und Auftrag. Am Fahrzeug wählt man den
 * Auftrag, am Auftrag das Fahrzeug.
 */
export function AssignmentsPanel({ vehicleId, orderId, defaultStart, defaultEnd, locked = false }: { vehicleId?: string; orderId?: string; defaultStart?: string | null; defaultEnd?: string | null; /** Der Auftrag ist abgeschlossen: Fahrzeug und Fahrer lassen sich nicht mehr ändern */ locked?: boolean }) {
  const { can } = useSession();
  const client = useQueryClient();
  const mayWrite = can('fleet.write');
  const employees = useEmployees();
  const vehicles = useVehicles();
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(false);
  const [vehicle, setVehicle] = useState(vehicleId ?? '');
  const [order, setOrder] = useState(orderId ?? '');
  const [driver, setDriver] = useState('');
  const [startsAt, setStartsAt] = useState(`${defaultStart ?? today()}T08:00`);
  const [endsAt, setEndsAt] = useState(`${defaultEnd ?? defaultStart ?? today()}T18:00`);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['fleet-assignments', { vehicleId, orderId }],
    queryFn: async () => (await api<Paginated<VehicleAssignment>>('GET', `/fleet/assignments${queryString({ vehicleId, orderId, perPage: 50 })}`)).data,
    refetchInterval: LIVE_MS,
    refetchOnWindowFocus: true,
  });
  const orders = useQuery({
    queryKey: ['fleet-order-options'],
    queryFn: async () => (await api<Paginated<OrderSummary>>('GET', '/orders?perPage=50&sort=-createdAt')).data,
    enabled: !orderId && adding && can('orders.read'),
    staleTime: 30_000,
  });
  const refresh = () => {
    for (const key of ['fleet-assignments', 'fleet-vehicles', 'fleet-vehicle', 'fleet-overview']) client.invalidateQueries({ queryKey: [key] });
  };
  const save = useMutation({
    mutationFn: () => {
      if (!vehicle) throw new Error('Bitte wählen Sie ein Fahrzeug.');
      return api('POST', '/fleet/assignments', { vehicleId: vehicle, orderId: order || null, employeeId: driver || null, startsAt, endsAt, note: note.trim() || null });
    },
    onSuccess: () => {
      refresh();
      setAdding(false);
      setNote('');
      setError(null);
    },
    onError: (e) => setError(e instanceof Error && !('status' in e) ? e.message : errorText(e, 'Der Einsatz konnte nicht eingeplant werden.')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `/fleet/assignments/${id}`),
    onSuccess: refresh,
    onError: (e) => setError(errorText(e, 'Der Einsatz konnte nicht gelöscht werden.')),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    save.mutate();
  };
  const now = nowWall();

  const content = (
    <>
      {locked && <p className="muted small">Der Auftrag ist abgeschlossen. Fahrzeug und Fahrer lassen sich nicht mehr ändern, sie bleiben im Fahrtprotokoll und im Fahrtenbuch erhalten.</p>}
      <p className="muted small">{orderId ? 'Solange der Zeitraum läuft, steht das Fahrzeug in der Flotte auf „Im Einsatz“, danach wieder auf „Frei“.' : 'Ein Fahrzeug ist „Im Einsatz“, solange ein Einsatz läuft, sonst „Frei“. Später kann auch die Mitarbeiter-App den Status melden.'}</p>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {adding && (
        <form className="fleet-form" onSubmit={submit} aria-label="Einsatz planen">
          <div className="form-grid">
            {!vehicleId && (
              <label className="field">
                <span>Fahrzeug</span>
                <select className="input" value={vehicle} onChange={(e) => setVehicle(e.target.value)} required>
                  <option value="">Bitte wählen</option>
                  {vehicles.data?.filter((v) => v.status === 'active').map((v) => <option key={v.id} value={v.id}>{v.plate}{v.name ? ` (${v.name})` : ''}{v.availability === 'in_use' ? ' – gerade im Einsatz' : ''}</option>)}
                </select>
              </label>
            )}
            {!orderId && can('orders.read') && (
              <label className="field">
                <span>Auftrag (optional)</span>
                <select className="input" value={order} onChange={(e) => setOrder(e.target.value)}>
                  <option value="">Ohne Auftrag</option>
                  {orders.data?.map((o) => <option key={o.id} value={o.id}>{o.number} · {o.subject}</option>)}
                </select>
              </label>
            )}
            <label className="field">
              <span>Fahrer (optional)</span>
              <select className="input" value={driver} onChange={(e) => setDriver(e.target.value)}>
                <option value="">Kein Fahrer</option>
                {employees.data?.map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
              </select>
            </label>
            <label className="field"><span>Von</span><input className="input" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} required /></label>
            <label className="field"><span>Bis</span><input className="input" type="datetime-local" value={endsAt} min={startsAt} onChange={(e) => setEndsAt(e.target.value)} required /></label>
            <label className="field field--wide"><span>Notiz (optional)</span><input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} /></label>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn--primary" disabled={save.isPending}>{save.isPending ? 'Wird geplant …' : 'Einplanen'}</button>
            <button type="button" className="btn btn--secondary" onClick={() => setAdding(false)}>Abbrechen</button>
          </div>
        </form>
      )}
      <div className="table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <th>Zeitraum</th>
              {!vehicleId && <th>Fahrzeug</th>}
              {!orderId && <th>Auftrag</th>}
              <th>Fahrer</th>
              <th>Stand</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={6} className="muted">Wird geladen …</td></tr>}
            {list.data?.length === 0 && <tr><td colSpan={6} className="empty">Noch kein Einsatz eingeplant.</td></tr>}
            {list.data?.map((a) => {
              const when = whenOf(a, now);
              return (
                <tr key={a.id} className={when === 'past' ? 'inv-reversed' : ''}>
                  <td data-label="Zeitraum">{wallFull(a.startsAt)} bis {wallFull(a.endsAt)}{a.note && <div className="muted small">{a.note}</div>}</td>
                  {!vehicleId && <td data-label="Fahrzeug"><Link to={`/flotte/${a.vehicleId}`}>{a.vehiclePlate}</Link></td>}
                  {!orderId && <td data-label="Auftrag">{a.orderId ? <Link to={`/auftraege/${a.orderId}`}>{a.orderNumber}</Link> : '–'}{a.orderSubject && <div className="muted small">{a.orderSubject}</div>}</td>}
                  <td data-label="Fahrer">{a.employeeName ?? '–'}</td>
                  <td data-label="Stand"><span className={`fleet-badge ${when === 'now' ? 'fleet-badge--use' : when === 'upcoming' ? 'fleet-badge--ok' : 'fleet-badge--off'}`}>{WHEN_LABEL[when]}</span></td>
                  <td className="doc-actions">{mayWrite && !locked && <button type="button" className="btn btn--danger btn--sm" onClick={() => window.confirm('Diesen Einsatz löschen?') && remove.mutate(a.id)}>Löschen</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
  if (vehicleId) {
    return (
      <CollapsibleCard title="Einsätze" actions={mayWrite && !locked && <button type="button" className="btn btn--secondary btn--sm" aria-expanded={adding} onClick={() => { setAdding(!adding); if (!adding) setOpen(true); }}>{orderId ? 'Fahrzeug zuweisen' : 'Einsatz planen'}</button>} open={open} onOpenChange={setOpen}>
        {content}
      </CollapsibleCard>
    );
  }

  return (
    <section className="card" aria-labelledby={`assign-${vehicleId ?? orderId}`}>
      <div className="section-head">
        <h2 id={`assign-${vehicleId ?? orderId}`}>{orderId ? 'Fahrzeug für diesen Auftrag' : 'Einsätze'}</h2>
        {mayWrite && !locked && <button type="button" className="btn btn--secondary btn--sm" aria-expanded={adding} onClick={() => setAdding(!adding)}>{orderId ? 'Fahrzeug zuweisen' : 'Einsatz planen'}</button>}
      </div>
      {content}
    </section>
  );
}
